import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toPaise, lineAmount, sumPaise } from '@vns/core';
import { supabase } from './supabase';
import { useSession } from './session';

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

/* ============================================================================
 * 1. CLIENT TRANSACTIONS (Record Payment)
 * ============================================================================ */

export interface ClientTxRow {
  id: string;
  date: string;
  projectId: string;
  projectName: string;
  clientName: string;
  projectValuePaise: number;
  creditPaise: number;
  debitPaise: number;
  mode: string;
  remarks: string | null;
  status: 'pending' | 'approved' | 'rejected';
}

export interface ClientPaymentDraft {
  date: string;
  projectId: string;
  projectValuePaise: number;
  txType: 'credit' | 'debit';
  amountRupees: string;
  mode: string;
  remarks: string;
}

export function useClientTransactions(search = '', projectId = '') {
  return useQuery({
    queryKey: ['client-transactions', search, projectId],
    refetchInterval: 4000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ClientTxRow[]> => {
      let q = supabase
        .from('client_payments')
        .select(`
          id, paid_on, amount_paise, payment_type, reference_no, remarks, approval_status, project_id,
          projects(name, value_paise, clients(name))
        `)
        .is('deleted_at', null)
        .order('paid_on', { ascending: false });

      if (projectId) {
        q = q.eq('project_id', projectId);
      }

      const rows = unwrap(await q);

      return rows.map((r) => {
        const proj = r.projects as unknown as {
          name: string;
          value_paise: number;
          clients: { name: string } | null;
        } | null;

        const isDebit =
          r.payment_type === 'other' &&
          (r.remarks?.toLowerCase().includes('debit') ||
            r.reference_no?.toLowerCase().includes('debit'));

        const creditPaise = isDebit ? 0 : Number(r.amount_paise);
        const debitPaise = isDebit ? Number(r.amount_paise) : 0;

        return {
          id: r.id,
          date: r.paid_on,
          projectId: r.project_id,
          projectName: proj?.name ?? '—',
          clientName: proj?.clients?.name ?? '—',
          projectValuePaise: Number(proj?.value_paise ?? 0),
          creditPaise,
          debitPaise,
          mode: r.reference_no || 'Cash',
          remarks: cleanDisplayRemarks(r.remarks),
          status: r.approval_status as ClientTxRow['status'],
        };
      }).filter((r) => {
        if (!search.trim()) return true;
        const term = search.toLowerCase();
        return (
          r.projectName.toLowerCase().includes(term) ||
          r.clientName.toLowerCase().includes(term) ||
          (r.remarks && r.remarks.toLowerCase().includes(term)) ||
          r.mode.toLowerCase().includes(term)
        );
      });
    },
  });
}

export interface ProjectPaymentSummary {
  totalReceivedPaise: number;
  paymentCount: number;
  paymentMap: Record<string, { amountPaise: number; isDebit: boolean }>;
}

export function useProjectClientPaymentSummary(projectId: string) {
  return useQuery({
    queryKey: ['project-client-payment-summary', projectId],
    enabled: Boolean(projectId),
    queryFn: async (): Promise<ProjectPaymentSummary> => {
      const { data, error } = await supabase
        .from('client_payments')
        .select('id, amount_paise, payment_type, reference_no, remarks')
        .eq('project_id', projectId)
        .is('deleted_at', null);

      if (error) throw new Error(error.message);

      let totalReceivedPaise = 0;
      const paymentMap: Record<string, { amountPaise: number; isDebit: boolean }> = {};

      for (const p of data ?? []) {
        const isDebit =
          p.payment_type === 'other' &&
          (p.remarks?.toLowerCase().includes('debit') ||
            p.reference_no?.toLowerCase().includes('debit'));
        const amt = Number(p.amount_paise || 0);
        if (isDebit) {
          totalReceivedPaise -= amt;
        } else {
          totalReceivedPaise += amt;
        }
        paymentMap[p.id] = { amountPaise: amt, isDebit: Boolean(isDebit) };
      }

      return {
        totalReceivedPaise: Math.max(0, totalReceivedPaise),
        paymentCount: data?.length ?? 0,
        paymentMap,
      };
    },
  });
}

export function useRecordClientPayment() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (draft: ClientPaymentDraft) => {
      if (!actor) throw new Error('Not signed in');
      if (!draft.projectId) throw new Error('Choose a project');
      if (!draft.date) throw new Error('Select a date');
      const amt = Number(draft.amountRupees.replace(/[₹,\s]/g, ''));
      if (!amt || amt <= 0) throw new Error('Enter a valid amount greater than zero');

      const amountPaise = toPaise(draft.amountRupees);
      const isDebit = draft.txType === 'debit';
      const referenceNo = `${draft.mode || 'Cash'}${isDebit ? ' [Debit]' : ''}`;
      const remarks = draft.remarks.trim() || null;

      const { data, error } = await supabase
        .from('client_payments')
        .insert({
          company_id: actor.companyId,
          project_id: draft.projectId,
          paid_on: draft.date,
          payment_type: isDebit ? 'other' : 'milestone',
          amount_paise: amountPaise,
          reference_no: referenceNo,
          remarks,
          approval_status: 'approved',
        })
        .select('id')
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['client-transactions'] }),
        qc.invalidateQueries({ queryKey: ['project-client-payment-summary'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['clients'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}

export function useUpdateClientPayment() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: ClientPaymentDraft }) => {
      if (!draft.projectId) throw new Error('Choose a project');
      if (!draft.date) throw new Error('Select a date');
      const amt = Number(draft.amountRupees.replace(/[₹,\s]/g, ''));
      if (!amt || amt <= 0) throw new Error('Enter a valid amount greater than zero');

      const amountPaise = toPaise(draft.amountRupees);
      const isDebit = draft.txType === 'debit';
      const referenceNo = `${draft.mode || 'Cash'}${isDebit ? ' [Debit]' : ''}`;
      const remarks = draft.remarks.trim() || null;

      const { data, error } = await supabase
        .from('client_payments')
        .update({
          project_id: draft.projectId,
          paid_on: draft.date,
          payment_type: isDebit ? 'other' : 'milestone',
          amount_paise: amountPaise,
          reference_no: referenceNo,
          remarks,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['client-transactions'] }),
        qc.invalidateQueries({ queryKey: ['project-client-payment-summary'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['clients'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}

async function tryRemoveStorageFile(urlOrPath?: string | null) {
  if (!urlOrPath) return;
  try {
    const match = urlOrPath.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)$/);
    if (match && match[1] && match[2]) {
      const bucket = match[1];
      const pathPart = match[2].split('?')[0];
      if (pathPart) {
        const path = decodeURIComponent(pathPart);
        await supabase.storage.from(bucket).remove([path]);
      }
    }
  } catch {
    // Ignore storage deletion errors
  }
}

export function useDeleteClientPayment() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (row: ClientTxRow) => {
      await tryRemoveStorageFile((row as unknown as { receiptUrl?: string })?.receiptUrl);

      const { error: delErr } = await supabase
        .from('client_payments')
        .delete()
        .eq('id', row.id);

      if (delErr) {
        const { error: softErr } = await supabase
          .from('client_payments')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', row.id);

        if (softErr) throw new Error(delErr.message || softErr.message);
      }
      return row.id;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['client-transactions'] }),
        qc.invalidateQueries({ queryKey: ['project-client-payment-summary'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['clients'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}

/* ============================================================================
 * 2. SUPPLIER TRANSACTIONS (Material Purchase)
 * ============================================================================ */

export interface SupplierTxLine {
  materialId: string;
  materialName: string;
  quantity: number;
  unit: string;
  unitId: string;
  ratePaise: number;
  amountPaise: number;
}

export interface SupplierTxRow {
  id: string;
  date: string;
  projectId: string;
  projectName: string;
  supplierId: string;
  supplierName: string;
  billNo: string | null;
  materialsSummary: string;
  linesCount: number;
  totalAmountPaise: number;
  paidAmountPaise: number;
  balanceAmountPaise: number;
  mode: string;
  remarks: string | null;
  status: 'pending' | 'approved' | 'rejected';
  lines: SupplierTxLine[];
}

export interface SupplierMaterialLineDraft {
  material_id: string;
  unit_id: string;
  unit_name: string;
  quantity: string;
  rateRupees: string;
  totalAmountPaise: number;
}

export interface SupplierPurchaseDraft {
  date: string;
  billDate?: string;
  clientId?: string;
  projectId: string;
  supplierId: string;
  txType?: 'credit' | 'debit';
  billNo: string;
  printedTotalRupees: string;
  lines: SupplierMaterialLineDraft[];
  paidAmountRupees: string;
  mode: string;
  remarks: string;
}

export async function fetchSupplierTransactions(search = '', projectId = '', supplierId = ''): Promise<SupplierTxRow[]> {
      let q = supabase
        .from('purchase_bills')
        .select(`
          id, bill_no, bill_date, bill_total_paise, approval_status, remarks, project_id, supplier_id,
          projects(name), suppliers(name),
          material_purchases(
            id, quantity, rate_paise, amount_paise, advance_paid_paise, remarks,
            materials(name), units(short_name, name)
          )
        `)
        .is('deleted_at', null)
        .order('bill_date', { ascending: false });

      if (projectId) q = q.eq('project_id', projectId);
      if (supplierId) q = q.eq('supplier_id', supplierId);

      let payQ = supabase
        .from('supplier_payments')
        .select(`
          id, supplier_id, project_id, amount_paise, reference_no, mode, paid_on, remarks, approval_status,
          projects(name), suppliers(name)
        `)
        .is('deleted_at', null)
        .order('paid_on', { ascending: false });

      if (projectId) payQ = payQ.eq('project_id', projectId);
      if (supplierId) payQ = payQ.eq('supplier_id', supplierId);

      const [billsRes, paymentsRes] = await Promise.all([q, payQ]);

      const bills = unwrap(billsRes);
      const payments = paymentsRes.data ?? [];

      const usedPaymentIds = new Set<string>();
      // Map payments by project + supplier or reference_no
      const paymentsByBillRef = new Map<string, { paidPaise: number; mode: string }>();
      payments.forEach((p) => {
        const ref = p.reference_no?.trim();
        if (ref) {
          const matchingBill = bills.find(
            (b) => (b.bill_no && b.bill_no.trim() === ref) || `Bill-${b.id.slice(0, 8)}` === ref,
          );
          if (matchingBill) {
            usedPaymentIds.add(p.id);
            const prev = paymentsByBillRef.get(ref) ?? { paidPaise: 0, mode: p.mode || 'Cash' };
            paymentsByBillRef.set(ref, {
              paidPaise: prev.paidPaise + Number(p.amount_paise),
              mode: p.mode || prev.mode,
            });
          }
        }
      });

      const billRows: SupplierTxRow[] = bills.map((b) => {
        const proj = b.projects as unknown as { name: string } | null;
        const supp = b.suppliers as unknown as { name: string } | null;

        const rawLines = (b.material_purchases ?? []) as unknown as Array<{
          id: string;
          quantity: number;
          rate_paise: number;
          amount_paise: number;
          advance_paid_paise: number;
          materials: { name: string } | null;
          units: { short_name: string; name: string } | null;
        }>;

        const lines: SupplierTxLine[] = rawLines.map((l) => ({
          materialId: l.id,
          materialName: l.materials?.name ?? 'Material',
          quantity: Number(l.quantity),
          unit: l.units?.short_name || l.units?.name || 'Nos',
          unitId: '',
          ratePaise: Number(l.rate_paise),
          amountPaise: Number(l.amount_paise),
        }));

        const linesSum = sumPaise(lines.map((l) => l.amountPaise));
        const totalAmountPaise = Number(b.bill_total_paise) || linesSum;

        // Advance recorded on lines
        const linesAdvance = sumPaise(rawLines.map((l) => Number(l.advance_paid_paise || 0)));

        // Payment by bill reference if recorded separately
        const billPay = b.bill_no ? paymentsByBillRef.get(b.bill_no) : undefined;
        const paidAmountPaise = Math.max(linesAdvance, billPay?.paidPaise ?? 0);
        const balanceAmountPaise = Math.max(0, totalAmountPaise - paidAmountPaise);

        const materialNames = lines.map((l) => l.materialName).slice(0, 3).join(', ');
        const materialsSummary =
          lines.length > 3 ? `${materialNames} +${lines.length - 3} more` : materialNames || 'Materials';

        return {
          id: b.id,
          date: b.bill_date,
          projectId: b.project_id ?? '',
          projectName: proj?.name ?? '—',
          supplierId: b.supplier_id,
          supplierName: supp?.name ?? '—',
          billNo: b.bill_no,
          materialsSummary,
          linesCount: lines.length,
          totalAmountPaise,
          paidAmountPaise,
          balanceAmountPaise,
          mode: billPay?.mode ? billPay.mode.toUpperCase() : 'CASH',
          remarks: cleanDisplayRemarks(b.remarks),
          status: b.approval_status as SupplierTxRow['status'],
          lines,
        };
      });

      // Standalone Debit payments (direct payment to supplier, not part of a bill)
      const standalonePaymentRows: SupplierTxRow[] = payments
        .filter((p) => !usedPaymentIds.has(p.id))
        .map((p) => {
          const proj = p.projects as unknown as { name: string } | null;
          const supp = p.suppliers as unknown as { name: string } | null;
          return {
            id: p.id,
            date: p.paid_on,
            projectId: p.project_id ?? '',
            projectName: proj?.name ?? '—',
            supplierId: p.supplier_id,
            supplierName: supp?.name ?? '—',
            billNo: p.reference_no ?? null,
            materialsSummary: 'Payment to Supplier (Debit)',
            linesCount: 0,
            totalAmountPaise: 0,
            paidAmountPaise: Number(p.amount_paise),
            balanceAmountPaise: 0,
            mode: p.mode ? String(p.mode).toUpperCase() : 'CASH',
            remarks: cleanDisplayRemarks(p.remarks),
            status: (p.approval_status as SupplierTxRow['status']) || 'approved',
            lines: [],
          };
        });

      return [...billRows, ...standalonePaymentRows]
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
        .filter((r) => {
          if (!search.trim()) return true;
          const term = search.toLowerCase();
          return (
            r.projectName.toLowerCase().includes(term) ||
            r.supplierName.toLowerCase().includes(term) ||
            (r.billNo && r.billNo.toLowerCase().includes(term)) ||
            r.materialsSummary.toLowerCase().includes(term) ||
            (r.remarks && r.remarks.toLowerCase().includes(term))
          );
        });
}

export function useSupplierTransactions(search = '', projectId = '', supplierId = '') {
  return useQuery({
    queryKey: ['supplier-transactions', search, projectId, supplierId],
    refetchInterval: 4000,
    refetchOnWindowFocus: true,
    queryFn: () => fetchSupplierTransactions(search, projectId, supplierId),
  });
}

export interface SupplierBalanceSummary {
  totalPurchasePaise: number;
  totalPaidPaise: number;
  balanceOwedPaise: number;
  projectBalanceOwedPaise: number;
  billMap: Record<string, { totalAmountPaise: number; paidAmountPaise: number }>;
  paymentMap: Record<string, { amountPaise: number }>;
}

export function useSupplierBalanceSummary(supplierId: string, projectId?: string) {
  return useQuery({
    queryKey: ['supplier-balance-summary', supplierId, projectId || 'all'],
    enabled: Boolean(supplierId),
    queryFn: async (): Promise<SupplierBalanceSummary> => {
      const billsQ = supabase
        .from('purchase_bills')
        .select(`
          id, bill_no, bill_total_paise, project_id,
          material_purchases(id, quantity, rate_paise, amount_paise, advance_paid_paise)
        `)
        .eq('supplier_id', supplierId)
        .is('deleted_at', null);

      const standaloneMatQ = supabase
        .from('material_purchases')
        .select('id, amount_paise, advance_paid_paise, project_id')
        .eq('supplier_id', supplierId)
        .is('purchase_bill_id', null)
        .is('deleted_at', null);

      const paymentsQ = supabase
        .from('supplier_payments')
        .select('id, amount_paise, reference_no, project_id')
        .eq('supplier_id', supplierId)
        .is('deleted_at', null);

      const [billsRes, standaloneRes, paymentsRes] = await Promise.all([billsQ, standaloneMatQ, paymentsQ]);
      if (billsRes.error) throw new Error(billsRes.error.message);
      if (paymentsRes.error) throw new Error(paymentsRes.error.message);

      const bills = billsRes.data ?? [];
      const standalonePurchases = standaloneRes.data ?? [];
      const payments = paymentsRes.data ?? [];

      const paymentsByRef = new Map<string, number>();
      payments.forEach((p) => {
        const ref = p.reference_no?.trim();
        if (ref) {
          paymentsByRef.set(ref, (paymentsByRef.get(ref) ?? 0) + Number(p.amount_paise || 0));
        }
      });

      let totalPurchasePaise = 0;
      let projectPurchasePaise = 0;
      const billMap: Record<string, { totalAmountPaise: number; paidAmountPaise: number }> = {};

      for (const b of bills) {
        const rawLines = (b.material_purchases ?? []) as Array<{
          quantity: number;
          rate_paise: number;
          amount_paise: number;
          advance_paid_paise: number;
        }>;
        const linesSum = rawLines.reduce(
          (s, l) => s + (Number(l.amount_paise) || Number(l.quantity) * Number(l.rate_paise || 0)),
          0,
        );
        const billTotal = Number(b.bill_total_paise) || linesSum;
        const linesAdvance = rawLines.reduce((s, l) => s + Number(l.advance_paid_paise || 0), 0);
        const refPay =
          (b.bill_no ? paymentsByRef.get(b.bill_no.trim()) : 0) ||
          paymentsByRef.get(`Bill-${b.id.slice(0, 8)}`) ||
          0;
        const paidForBill = Math.max(linesAdvance, refPay);

        totalPurchasePaise += billTotal;
        if (projectId && b.project_id === projectId) {
          projectPurchasePaise += billTotal;
        }

        billMap[b.id] = {
          totalAmountPaise: billTotal,
          paidAmountPaise: paidForBill,
        };
      }

      for (const sp of standalonePurchases) {
        const amt = Number(sp.amount_paise || 0);
        totalPurchasePaise += amt;
        if (projectId && sp.project_id === projectId) {
          projectPurchasePaise += amt;
        }
      }

      let totalPaidPaise = 0;
      let projectPaidPaise = 0;
      const paymentMap: Record<string, { amountPaise: number }> = {};

      for (const p of payments) {
        const amt = Number(p.amount_paise || 0);
        totalPaidPaise += amt;
        if (projectId && p.project_id === projectId) {
          projectPaidPaise += amt;
        }
        paymentMap[p.id] = { amountPaise: amt };
      }

      const balanceOwedPaise = Math.max(0, totalPurchasePaise - totalPaidPaise);
      const projectBalanceOwedPaise = Math.max(0, projectPurchasePaise - projectPaidPaise);

      return {
        totalPurchasePaise,
        totalPaidPaise,
        balanceOwedPaise,
        projectBalanceOwedPaise,
        billMap,
        paymentMap,
      };
    },
  });
}

export function useRecordSupplierPurchase() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (draft: SupplierPurchaseDraft) => {
      if (!actor) throw new Error('Not signed in');
      if (!draft.projectId) throw new Error('Choose a project');
      if (!draft.supplierId) throw new Error('Choose a supplier');
      if (!draft.date) throw new Error('Select a date');

      // Standalone Debit payment to supplier
      if (draft.txType === 'debit') {
        const amtPaise = toPaise(draft.paidAmountRupees);
        if (amtPaise <= 0) throw new Error('Enter a valid payment amount greater than zero');
        const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
        const { data, error } = await supabase
          .from('supplier_payments')
          .insert({
            company_id: actor.companyId,
            supplier_id: draft.supplierId,
            project_id: draft.projectId,
            paid_on: draft.date,
            amount_paise: amtPaise,
            mode: modeVal,
            reference_no: draft.billNo.trim() || null,
            remarks: draft.remarks.trim() || null,
            approval_status: 'approved',
          })
          .select('id')
          .single();

        if (error) throw new Error(error.message);
        return data.id;
      }

      const validLines = draft.lines.filter(
        (l) => l.material_id && Number(l.quantity) > 0 && Number(l.rateRupees.replace(/[₹,\s]/g, '')) >= 0,
      );

      if (validLines.length === 0) {
        throw new Error('Please add at least one valid material line with quantity and rate');
      }

      // Calculate total amount
      const computedTotalPaise = sumPaise(
        validLines.map((l) => lineAmount(Number(l.quantity), toPaise(l.rateRupees))),
      );

      const printedTotalPaise = draft.printedTotalRupees.trim()
        ? toPaise(draft.printedTotalRupees)
        : computedTotalPaise;

      // Handle paid amount & advance allocation
      const paidPaise = draft.paidAmountRupees.trim() ? toPaise(draft.paidAmountRupees) : 0;

      // Distribute advance across lines for DB constraint
      let remainingAdvance = paidPaise;
      const rpcLines = validLines.map((l) => {
        const lineAmt = lineAmount(Number(l.quantity), toPaise(l.rateRupees));
        const advanceForLine = Math.min(remainingAdvance, lineAmt);
        remainingAdvance = Math.max(0, remainingAdvance - advanceForLine);

        return {
          material_id: l.material_id,
          quantity: Number(l.quantity),
          unit_id: l.unit_id || undefined,
          rate_paise: toPaise(l.rateRupees),
          advance_paid_paise: advanceForLine,
        };
      });

      // 1. Create Purchase Bill with lines via atomic RPC
      const { data: billId, error: billErr } = await supabase.rpc('create_purchase_bill', {
        p_project_id: draft.projectId,
        p_supplier_id: draft.supplierId,
        p_bill_date: draft.billDate || draft.date,
        p_lines: rpcLines,
        p_bill_no: draft.billNo.trim() || undefined,
        p_bill_total_paise: printedTotalPaise,
        p_remarks: draft.remarks.trim() || undefined,
      });

      if (billErr) throw new Error(billErr.message);

      // 2. If money was paid, also record in supplier_payments to guarantee accounting ledger consistency
      if (paidPaise > 0) {
        const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
        await supabase.from('supplier_payments').insert({
          company_id: actor.companyId,
          supplier_id: draft.supplierId,
          project_id: draft.projectId,
          paid_on: draft.date,
          amount_paise: paidPaise,
          mode: modeVal,
          reference_no: draft.billNo.trim() || `Bill-${String(billId).slice(0, 8)}`,
          remarks: draft.remarks.trim() || null,
          approval_status: 'approved',
        });
      }

      return billId;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['supplier-transactions'] }),
        qc.invalidateQueries({ queryKey: ['supplier-balance-summary'] }),
        qc.invalidateQueries({ queryKey: ['bills'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['suppliers'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['materials'] }),
      ]);
    },
  });
}

export function useUpdateSupplierPurchase() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: SupplierPurchaseDraft }) => {
      if (!actor) throw new Error('Not signed in');
      if (!draft.projectId) throw new Error('Choose a project');
      if (!draft.supplierId) throw new Error('Choose a supplier');
      if (!draft.date) throw new Error('Select a date');

      // Standalone Debit update
      if (draft.txType === 'debit') {
        const amtPaise = toPaise(draft.paidAmountRupees);
        if (amtPaise <= 0) throw new Error('Enter a valid payment amount greater than zero');
        const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
        const { error } = await supabase
          .from('supplier_payments')
          .update({
            project_id: draft.projectId,
            supplier_id: draft.supplierId,
            paid_on: draft.date,
            amount_paise: amtPaise,
            mode: modeVal,
            reference_no: draft.billNo.trim() || null,
            remarks: draft.remarks.trim() || null,
          })
          .eq('id', id);

        if (error) throw new Error(error.message);
        return id;
      }

      const validLines = draft.lines.filter(
        (l) => l.material_id && Number(l.quantity) > 0 && Number(l.rateRupees.replace(/[₹,\s]/g, '')) >= 0,
      );
      if (validLines.length === 0) {
        throw new Error('Please add at least one valid material line with quantity and rate');
      }

      const computedTotalPaise = sumPaise(
        validLines.map((l) => lineAmount(Number(l.quantity), toPaise(l.rateRupees))),
      );
      const printedTotalPaise = draft.printedTotalRupees.trim()
        ? toPaise(draft.printedTotalRupees)
        : computedTotalPaise;
      const paidPaise = draft.paidAmountRupees.trim() ? toPaise(draft.paidAmountRupees) : 0;

      // 1. Update purchase_bills
      const { error: billErr } = await supabase
        .from('purchase_bills')
        .update({
          project_id: draft.projectId,
          supplier_id: draft.supplierId,
          bill_date: draft.billDate || draft.date,
          bill_no: draft.billNo.trim() || null,
          bill_total_paise: printedTotalPaise,
          remarks: draft.remarks.trim() || null,
        })
        .eq('id', id);

      if (billErr) throw new Error(billErr.message);

      // 2. Remove old material_purchases lines for this bill
      await supabase.from('material_purchases').delete().eq('bill_id', id);

      // 3. Insert new material_purchases lines
      let remainingAdvance = paidPaise;
      const newLines = validLines.map((l) => {
        const lineAmt = lineAmount(Number(l.quantity), toPaise(l.rateRupees));
        const advanceForLine = Math.min(remainingAdvance, lineAmt);
        remainingAdvance = Math.max(0, remainingAdvance - advanceForLine);

        return {
          company_id: actor.companyId,
          project_id: draft.projectId,
          supplier_id: draft.supplierId,
          material_id: l.material_id,
          bill_id: id,
          purchased_on: draft.billDate || draft.date,
          quantity: Number(l.quantity),
          unit_id: l.unit_id || null,
          rate_paise: toPaise(l.rateRupees),
          amount_paise: lineAmt,
          advance_paid_paise: advanceForLine,
          invoice_no: draft.billNo.trim() || null,
          remarks: draft.remarks.trim() || null,
        };
      });

      const { error: linesErr } = await supabase.from('material_purchases').insert(newLines);
      if (linesErr) throw new Error(linesErr.message);

      // 4. Update supplier_payments if any payment was attached
      const refNo = draft.billNo.trim() || `Bill-${id.slice(0, 8)}`;
      if (paidPaise > 0) {
        const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
        const { data: existingPay } = await supabase
          .from('supplier_payments')
          .select('id')
          .eq('project_id', draft.projectId)
          .eq('supplier_id', draft.supplierId)
          .eq('reference_no', refNo)
          .maybeSingle();

        if (existingPay?.id) {
          await supabase
            .from('supplier_payments')
            .update({
              paid_on: draft.date,
              amount_paise: paidPaise,
              mode: modeVal,
              remarks: draft.remarks.trim() || null,
            })
            .eq('id', existingPay.id);
        } else {
          await supabase.from('supplier_payments').insert({
            company_id: actor.companyId,
            supplier_id: draft.supplierId,
            project_id: draft.projectId,
            paid_on: draft.date,
            amount_paise: paidPaise,
            mode: modeVal,
            reference_no: refNo,
            remarks: draft.remarks.trim() || null,
            approval_status: 'approved',
          });
        }
      }

      return id;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['supplier-transactions'] }),
        qc.invalidateQueries({ queryKey: ['supplier-balance-summary'] }),
        qc.invalidateQueries({ queryKey: ['bills'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['suppliers'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['materials'] }),
      ]);
    },
  });
}

export function useDeleteSupplierTransaction() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (row: SupplierTxRow) => {
      await tryRemoveStorageFile((row as unknown as { fileUrl?: string })?.fileUrl);

      const isStandalone = row.linesCount === 0 && row.totalAmountPaise === 0;

      if (isStandalone) {
        const { error: delErr } = await supabase
          .from('supplier_payments')
          .delete()
          .eq('id', row.id);
        if (delErr) {
          await supabase
            .from('supplier_payments')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', row.id);
        }
      } else {
        // Delete child material_purchases
        await supabase
          .from('material_purchases')
          .delete()
          .eq('purchase_bill_id', row.id);

        if (row.billNo) {
          await supabase
            .from('supplier_payments')
            .delete()
            .eq('reference_no', row.billNo);
        }
        await supabase
          .from('supplier_payments')
          .delete()
          .eq('reference_no', `Bill-${row.id.slice(0, 8)}`);

        const { error: billErr } = await supabase
          .from('purchase_bills')
          .delete()
          .eq('id', row.id);

        if (billErr) {
          await supabase
            .from('purchase_bills')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', row.id);
          await supabase
            .from('material_purchases')
            .update({ deleted_at: new Date().toISOString() })
            .eq('purchase_bill_id', row.id);
        }
      }

      return row.id;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['supplier-transactions'] }),
        qc.invalidateQueries({ queryKey: ['supplier-balance-summary'] }),
        qc.invalidateQueries({ queryKey: ['bills'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['suppliers'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['materials'] }),
      ]);
    },
  });
}

/* ============================================================================
 * 3. SUB CONTRACTOR TRANSACTIONS (Sub contractor labour)
 * ============================================================================ */

export interface SubcontractorTxWorkLine {
  id: string;
  workTypeName: string;
  workerCount: number;
  ratePerWorkerPaise: number;
  totalAmountPaise: number;
  remarks: string | null;
}

export interface SubcontractorTxRow {
  id: string;
  date: string;
  createdAt?: string;
  projectId: string;
  projectName: string;
  subcontractorId: string;
  subcontractorName: string;
  tradesSummary: string;
  totalWorkers: number;
  overallTotalPaise: number;
  paidAmountPaise: number;
  balanceAmountPaise: number;
  remarks: string | null;
  status: 'pending' | 'approved' | 'rejected';
  lines: SubcontractorTxWorkLine[];
}

export interface SubcontractorWorkLineDraft {
  work_type_id: string;
  worker_count: string;
  daily_rate: string;
  totalAmountPaise: number;
  remarks: string;
  custom_work_name?: string;
  is_custom?: boolean;
}

export interface SubcontractorIndividualWorkLineDraft {
  nameOfWork: string;
  workVolume: string;
  unit: string;
  rate: string;
}

export interface SubcontractorLabourDraft {
  date: string;
  clientId?: string;
  projectId: string;
  subcontractorId: string;
  txType?: 'credit' | 'debit';
  lines: SubcontractorWorkLineDraft[];
  paidAmountRupees: string;
  mode: string;
  remarks: string;
  // Individual entry fields
  entryMode?: 'weekly' | 'individual';
  nameOfWork?: string;
  workVolume?: string;
  unit?: string;
  rate?: string;
  creditAmountRupees?: string;
  individualLines?: SubcontractorIndividualWorkLineDraft[];
}

export function cleanWorkTypeName(name?: string | null): string {
  if (!name) return 'Trade Work';
  return name.replace(/\s*\((?:Visit|Entry|Bill)\s*\d+\)/gi, '').trim() || name;
}

export function cleanDisplayRemarks(remarks?: string | null): string | null {
  if (!remarks) return null;
  const stripped = remarks
    .replace(/\[Category:[^\]]*\]\s*-?\s*/gi, '')
    .replace(/^Category:\s*[^|•\n]*(?:[|•]\s*)?/i, '')
    .replace(/\[Individual[^\]]*\]\s*-?\s*/gi, '')
    .replace(/\[Weekly[^\]]*\]\s*-?\s*/gi, '')
    .replace(/^Payment for [^-\n]*(?:-\s*)?/i, '')
    .replace(/^Payment on labour visit of [^-\n]*(?:-\s*)?/i, '')
    .replace(/^Weekly credit$/i, '')
    .replace(/^Supplier payment \[Debit\]$/i, '')
    .replace(/^Subcontractor payment \[Debit\]$/i, '')
    .replace(/^Refund to Client \(Debit Entry\)$/i, '')
    .replace(/\s*\[Debit\]/gi, '')
    .replace(/\s*\(Debit\/Refund Entry\)/gi, '')
    .replace(/^Mode:\s*[^•\n]*(?:•\s*)?/i, '')
    .trim();
  return stripped || null;
}

export interface RawBatchInfo {
  id: string;
  subcontractorId: string;
  projectId: string;
  date: string;
  overallTotalPaise: number;
}

export interface RawPaymentInfo {
  id: string;
  subcontractorId: string;
  projectId: string;
  paidOn: string;
  amountPaise: number;
  referenceNo?: string | null;
}

export function allocateSubcontractorPayments<T extends RawBatchInfo, P extends RawPaymentInfo>(
  batches: T[],
  payments: P[],
): {
  batchPaidMap: Map<string, number>;
  usedPaymentIds: Set<string>;
} {
  const batchPaidMap = new Map<string, number>();
  const usedPaymentIds = new Set<string>();

  for (const b of batches) {
    batchPaidMap.set(b.id, 0);
  }

  // 1. Direct exact-match allocation where p.referenceNo matches batch.id
  for (const p of payments) {
    if (p.referenceNo && batchPaidMap.has(p.referenceNo)) {
      const currentPaid = batchPaidMap.get(p.referenceNo) ?? 0;
      batchPaidMap.set(p.referenceNo, currentPaid + p.amountPaise);
      usedPaymentIds.add(p.id);
    }
  }

  // 2. Pool unlinked payments by subcontractor + date + project
  const unlinkedPoolByDay = new Map<string, { totalPaise: number; paymentIds: string[] }>();
  for (const p of payments) {
    if (usedPaymentIds.has(p.id)) continue;
    const key = `${p.subcontractorId}_${p.paidOn}_${p.projectId || ''}`;
    const pool = unlinkedPoolByDay.get(key) ?? { totalPaise: 0, paymentIds: [] };
    pool.totalPaise += p.amountPaise;
    pool.paymentIds.push(p.id);
    unlinkedPoolByDay.set(key, pool);
  }

  // 3. Distribute unlinked pool to batches on that date up to remaining balance
  for (const b of batches) {
    const key = `${b.subcontractorId}_${b.date}_${b.projectId || ''}`;
    const pool = unlinkedPoolByDay.get(key);
    if (!pool || pool.totalPaise <= 0) continue;

    const alreadyPaid = batchPaidMap.get(b.id) ?? 0;
    const remainingBalance = Math.max(0, b.overallTotalPaise - alreadyPaid);
    if (remainingBalance <= 0) continue;

    const alloc = Math.min(remainingBalance, pool.totalPaise);
    batchPaidMap.set(b.id, alreadyPaid + alloc);
    pool.totalPaise -= alloc;
    pool.paymentIds.forEach((id) => usedPaymentIds.add(id));
  }

  // 4. If pool still has excess payments, allocate to last batch on that day
  for (const [key, pool] of unlinkedPoolByDay.entries()) {
    if (pool.totalPaise > 0) {
      const dayBatches = batches.filter(
        (b) => `${b.subcontractorId}_${b.date}_${b.projectId || ''}` === key,
      );
      if (dayBatches.length > 0) {
        const lastBatch = dayBatches[dayBatches.length - 1];
        if (lastBatch) {
          const currentPaid = batchPaidMap.get(lastBatch.id) ?? 0;
          batchPaidMap.set(lastBatch.id, currentPaid + pool.totalPaise);
          pool.paymentIds.forEach((id) => usedPaymentIds.add(id));
        }
      }
    }
  }

  return { batchPaidMap, usedPaymentIds };
}

async function resolveUniqueTradeId(
  companyId: string,
  projectId: string,
  subcontractorId: string,
  workedOn: string,
  baseWorkTypeId: string,
  excludeBatchId?: string,
  usedInThisBatch?: Set<string>,
): Promise<string> {
  let q = supabase
    .from('subcontractor_labour')
    .select('id, batch_id')
    .eq('project_id', projectId)
    .eq('subcontractor_id', subcontractorId)
    .eq('work_type_id', baseWorkTypeId)
    .eq('worked_on', workedOn)
    .is('deleted_at', null);

  if (excludeBatchId) {
    q = q.neq('batch_id', excludeBatchId);
  }

  const { data: existingCollisions } = await q;
  const isUsedInBatch = usedInThisBatch ? usedInThisBatch.has(baseWorkTypeId) : false;
  const hasCollision = isUsedInBatch || (existingCollisions && existingCollisions.length > 0);

  if (!hasCollision) {
    return baseWorkTypeId;
  }

  const { data: baseWt } = await supabase
    .from('work_types')
    .select('name')
    .eq('id', baseWorkTypeId)
    .single();

  const baseName = cleanWorkTypeName(baseWt?.name || 'Trade Work');

  for (let visitNum = 2; visitNum <= 25; visitNum++) {
    const aliasName = `${baseName} (Visit ${visitNum})`;
    let aliasId = '';

    const { data: existingAlias } = await supabase
      .from('work_types')
      .select('id')
      .ilike('name', aliasName)
      .maybeSingle();

    if (existingAlias?.id) {
      aliasId = existingAlias.id;
    } else {
      const { data: newWt, error: wtErr } = await supabase
        .from('work_types')
        .insert({
          company_id: companyId,
          name: aliasName,
          sort_order: 100,
        })
        .select('id')
        .single();
      if (wtErr) continue;
      aliasId = newWt.id;
    }

    if (usedInThisBatch && usedInThisBatch.has(aliasId)) {
      continue;
    }

    let aliasCheck = supabase
      .from('subcontractor_labour')
      .select('id')
      .eq('project_id', projectId)
      .eq('subcontractor_id', subcontractorId)
      .eq('work_type_id', aliasId)
      .eq('worked_on', workedOn)
      .is('deleted_at', null);

    if (excludeBatchId) {
      aliasCheck = aliasCheck.neq('batch_id', excludeBatchId);
    }
    const { data: aliasCollisions } = await aliasCheck;
    if (!aliasCollisions || aliasCollisions.length === 0) {
      return aliasId;
    }
  }

  return baseWorkTypeId;
}

export function computeSubcontractorRunningBalances(allRows: SubcontractorTxRow[]): SubcontractorTxRow[] {
  // Group rows by party: subcontractor + project
  const rowsByParty = new Map<string, SubcontractorTxRow[]>();
  for (const r of allRows) {
    const partyKey = `${r.subcontractorId}_${r.projectId || ''}`;
    const group = rowsByParty.get(partyKey) ?? [];
    group.push(r);
    rowsByParty.set(partyKey, group);
  }

  for (const partyRows of rowsByParty.values()) {
    partyRows.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date < b.date ? -1 : 1;
      }
      const aCreated = a.createdAt || '';
      const bCreated = b.createdAt || '';
      if (aCreated !== bCreated) {
        return aCreated < bCreated ? -1 : 1;
      }
      return a.id < b.id ? -1 : 1;
    });

    let runningBalancePaise = 0;
    for (const row of partyRows) {
      runningBalancePaise = runningBalancePaise + row.overallTotalPaise - row.paidAmountPaise;
      row.balanceAmountPaise = Math.max(0, runningBalancePaise);
    }
  }

  return allRows.sort((a, b) => {
    if (a.date !== b.date) {
      return a.date < b.date ? 1 : -1;
    }
    const aCreated = a.createdAt || '';
    const bCreated = b.createdAt || '';
    if (aCreated !== bCreated) {
      return aCreated < bCreated ? 1 : -1;
    }
    return a.id < b.id ? 1 : -1;
  });
}

export async function fetchSubcontractorTransactions(options?: {
  search?: string;
  projectId?: string;
  subcontractorId?: string;
  from?: string;
  to?: string;
}): Promise<SubcontractorTxRow[]> {
  const { search = '', projectId = '', subcontractorId = '', from, to } = options ?? {};
  let q = supabase
    .from('subcontractor_labour_batches')
    .select(`
      id, worked_on, created_at, remarks, approval_status, project_id, subcontractor_id,
      projects(name), subcontractors(name),
      subcontractor_labour(
        id, worker_count, rate_per_worker_paise, total_amount_paise, remarks,
        work_types(name)
      )
    `)
    .is('deleted_at', null)
    .order('worked_on', { ascending: false });

  if (projectId) q = q.eq('project_id', projectId);
  if (subcontractorId) q = q.eq('subcontractor_id', subcontractorId);

  let payQ = supabase
    .from('subcontractor_payments')
    .select(`
      id, subcontractor_id, project_id, amount_paise, paid_on, created_at, reference_no, remarks, mode, approval_status,
      projects(name), subcontractors(name)
    `)
    .is('deleted_at', null)
    .order('paid_on', { ascending: false });

  if (projectId) payQ = payQ.eq('project_id', projectId);
  if (subcontractorId) payQ = payQ.eq('subcontractor_id', subcontractorId);

  const [batchesRes, paymentsRes] = await Promise.all([q, payQ]);

  const batches = unwrap(batchesRes);
  const payments = paymentsRes.data ?? [];

  const batchRowsData = batches.map((b) => {
    const rawLines = (b.subcontractor_labour ?? []) as unknown as Array<{
      id: string;
      worker_count: number;
      rate_per_worker_paise: number | null;
      total_amount_paise: number | null;
      remarks: string | null;
      work_types: { name: string } | null;
    }>;

    let totalWorkers = 0;
    let overallTotalPaise = 0;

    const lines: SubcontractorTxWorkLine[] = rawLines.map((l) => {
      const count = Number(l.worker_count);
      totalWorkers += count;
      const rate = Number(l.rate_per_worker_paise ?? 0);
      const lineTotal = Number(l.total_amount_paise) || count * rate;
      overallTotalPaise += lineTotal;

      return {
        id: l.id,
        workTypeName: cleanWorkTypeName(l.work_types?.name),
        workerCount: count,
        ratePerWorkerPaise: rate,
        totalAmountPaise: lineTotal,
        remarks: cleanDisplayRemarks(l.remarks),
      };
    });

    return {
      b,
      lines,
      totalWorkers,
      overallTotalPaise,
    };
  });

  const { batchPaidMap, usedPaymentIds } = allocateSubcontractorPayments(
    batchRowsData.map(({ b, overallTotalPaise }) => ({
      id: b.id,
      subcontractorId: b.subcontractor_id,
      projectId: b.project_id ?? '',
      date: b.worked_on,
      overallTotalPaise,
    })),
    payments.map((p) => ({
      id: p.id,
      subcontractorId: p.subcontractor_id,
      projectId: p.project_id ?? '',
      paidOn: p.paid_on,
      amountPaise: Number(p.amount_paise || 0),
      referenceNo: p.reference_no,
    })),
  );

  const batchRows: SubcontractorTxRow[] = batchRowsData.map(({ b, lines, totalWorkers, overallTotalPaise }) => {
    const proj = b.projects as unknown as { name: string } | null;
    const sub = b.subcontractors as unknown as { name: string } | null;

    const paidAmountPaise = batchPaidMap.get(b.id) ?? 0;

    const tradeNames = lines
      .map((l) => `${l.workTypeName} (${l.workerCount})`)
      .slice(0, 3)
      .join(', ');
    const tradesSummary =
      lines.length > 3 ? `${tradeNames} +${lines.length - 3} more` : tradeNames || 'Labour Work';

    return {
      id: b.id,
      date: b.worked_on,
      createdAt: (b as unknown as { created_at?: string }).created_at,
      projectId: b.project_id ?? '',
      projectName: proj?.name ?? '—',
      subcontractorId: b.subcontractor_id,
      subcontractorName: sub?.name ?? '—',
      tradesSummary,
      totalWorkers,
      overallTotalPaise,
      paidAmountPaise,
      balanceAmountPaise: 0,
      remarks: cleanDisplayRemarks(b.remarks),
      status: b.approval_status as SubcontractorTxRow['status'],
      lines,
    };
  });

  // Standalone Debit payments (direct labour payments to subcontractor, not part of a daily batch)
  const standalonePaymentRows: SubcontractorTxRow[] = payments
    .filter((p) => !usedPaymentIds.has(p.id))
    .map((p) => {
      const proj = p.projects as unknown as { name: string } | null;
      const sub = p.subcontractors as unknown as { name: string } | null;
      return {
        id: p.id,
        date: p.paid_on,
        createdAt: p.created_at,
        projectId: p.project_id ?? '',
        projectName: proj?.name ?? '—',
        subcontractorId: p.subcontractor_id,
        subcontractorName: sub?.name ?? '—',
        tradesSummary: 'Payment to Sub contractor (Debit)',
        totalWorkers: 0,
        overallTotalPaise: 0,
        paidAmountPaise: Number(p.amount_paise),
        balanceAmountPaise: 0,
        remarks: cleanDisplayRemarks(p.remarks),
        status: (p.approval_status as SubcontractorTxRow['status']) || 'approved',
        lines: [],
      };
    });

  let results = computeSubcontractorRunningBalances([...batchRows, ...standalonePaymentRows]);

  if (from) {
    results = results.filter((r) => r.date >= from);
  }
  if (to) {
    results = results.filter((r) => r.date <= to);
  }

  if (search.trim()) {
    const term = search.toLowerCase();
    results = results.filter((r) => (
      r.projectName.toLowerCase().includes(term) ||
      r.subcontractorName.toLowerCase().includes(term) ||
      r.tradesSummary.toLowerCase().includes(term) ||
      (r.remarks && r.remarks.toLowerCase().includes(term))
    ));
  }

  return results;
}

export function useSubcontractorTransactions(search = '', projectId = '', subcontractorId = '') {
  return useQuery({
    queryKey: ['subcontractor-transactions', search, projectId, subcontractorId],
    refetchInterval: 4000,
    refetchOnWindowFocus: true,
    queryFn: () => fetchSubcontractorTransactions({ search, projectId, subcontractorId }),
  });
}

export interface SubcontractorBalanceSummary {
  totalLabourPaise: number;
  totalPaidPaise: number;
  balanceOwedPaise: number;
  projectBalanceOwedPaise: number;
  batchMap: Record<string, { totalAmountPaise: number; paidAmountPaise: number }>;
  paymentMap: Record<string, { amountPaise: number }>;
}

export function useSubcontractorBalanceSummary(subcontractorId: string, projectId?: string) {
  return useQuery({
    queryKey: ['subcontractor-balance-summary', subcontractorId, projectId || 'all'],
    enabled: Boolean(subcontractorId),
    queryFn: async (): Promise<SubcontractorBalanceSummary> => {
      const batchesQ = supabase
        .from('subcontractor_labour_batches')
        .select(`
          id, worked_on, project_id,
          subcontractor_labour(id, worker_count, rate_per_worker_paise, total_amount_paise)
        `)
        .eq('subcontractor_id', subcontractorId)
        .is('deleted_at', null);

      const standaloneLabourQ = supabase
        .from('subcontractor_labour')
        .select('id, worker_count, rate_per_worker_paise, total_amount_paise, project_id')
        .eq('subcontractor_id', subcontractorId)
        .is('batch_id', null)
        .is('deleted_at', null);

      const paymentsQ = supabase
        .from('subcontractor_payments')
        .select('id, amount_paise, project_id, paid_on, reference_no')
        .eq('subcontractor_id', subcontractorId)
        .is('deleted_at', null);

      const [batchesRes, standaloneRes, paymentsRes] = await Promise.all([batchesQ, standaloneLabourQ, paymentsQ]);
      if (batchesRes.error) throw new Error(batchesRes.error.message);
      if (paymentsRes.error) throw new Error(paymentsRes.error.message);

      const batches = batchesRes.data ?? [];
      const standaloneLabour = standaloneRes.data ?? [];
      const payments = paymentsRes.data ?? [];

      let totalLabourPaise = 0;
      let projectLabourPaise = 0;
      const batchListForAlloc: RawBatchInfo[] = [];

      for (const b of batches) {
        const rawLines = (b.subcontractor_labour ?? []) as Array<{
          worker_count: number;
          rate_per_worker_paise: number | null;
          total_amount_paise: number | null;
        }>;
        let batchTotal = 0;
        for (const l of rawLines) {
          const count = Number(l.worker_count);
          const rate = Number(l.rate_per_worker_paise ?? 0);
          batchTotal += Number(l.total_amount_paise) || count * rate;
        }

        totalLabourPaise += batchTotal;
        if (projectId && b.project_id === projectId) {
          projectLabourPaise += batchTotal;
        }

        batchListForAlloc.push({
          id: b.id,
          subcontractorId,
          projectId: b.project_id ?? '',
          date: b.worked_on,
          overallTotalPaise: batchTotal,
        });
      }

      const { batchPaidMap } = allocateSubcontractorPayments(
        batchListForAlloc,
        payments.map((p) => ({
          id: p.id,
          subcontractorId,
          projectId: p.project_id ?? '',
          paidOn: p.paid_on,
          amountPaise: Number(p.amount_paise || 0),
          referenceNo: p.reference_no,
        })),
      );

      const batchMap: Record<string, { totalAmountPaise: number; paidAmountPaise: number }> = {};
      for (const b of batchListForAlloc) {
        batchMap[b.id] = {
          totalAmountPaise: b.overallTotalPaise,
          paidAmountPaise: batchPaidMap.get(b.id) ?? 0,
        };
      }

      for (const sl of standaloneLabour) {
        const count = Number(sl.worker_count);
        const rate = Number(sl.rate_per_worker_paise ?? 0);
        const lineTotal = Number(sl.total_amount_paise) || count * rate;
        totalLabourPaise += lineTotal;
        if (projectId && sl.project_id === projectId) {
          projectLabourPaise += lineTotal;
        }
      }

      let totalPaidPaise = 0;
      let projectPaidPaise = 0;
      const paymentMap: Record<string, { amountPaise: number }> = {};

      for (const p of payments) {
        const amt = Number(p.amount_paise || 0);
        totalPaidPaise += amt;
        if (projectId && p.project_id === projectId) {
          projectPaidPaise += amt;
        }
        paymentMap[p.id] = { amountPaise: amt };
      }

      const balanceOwedPaise = Math.max(0, totalLabourPaise - totalPaidPaise);
      const projectBalanceOwedPaise = Math.max(0, projectLabourPaise - projectPaidPaise);

      return {
        totalLabourPaise,
        totalPaidPaise,
        balanceOwedPaise,
        projectBalanceOwedPaise,
        batchMap,
        paymentMap,
      };
    },
  });
}

export function useRecordSubcontractorLabour() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (draft: SubcontractorLabourDraft) => {
      if (!actor) throw new Error('Not signed in');
      if (!draft.projectId) throw new Error('Choose a project');
      if (!draft.subcontractorId) throw new Error('Choose a sub-contractor');
      if (!draft.date) throw new Error('Select a date');

      // Standalone Debit payment to subcontractor (e.g. Weekly Payment Debit)
      if (draft.txType === 'debit') {
        const amtPaise = toPaise(draft.paidAmountRupees);
        if (amtPaise <= 0) throw new Error('Enter a valid payment amount greater than zero');
        const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
        const debitNote = draft.remarks.trim() || null;
        const { data, error } = await supabase
          .from('subcontractor_payments')
          .insert({
            company_id: actor.companyId,
            subcontractor_id: draft.subcontractorId,
            project_id: draft.projectId,
            paid_on: draft.date,
            amount_paise: amtPaise,
            mode: modeVal,
            remarks: debitNote,
            approval_status: 'approved',
          })
          .select('id')
          .single();

        if (error) throw new Error(error.message);
        return data.id;
      }

      // Individual Piece-rate Entry
      if (draft.entryMode === 'individual') {
        const indLines =
          draft.individualLines && draft.individualLines.length > 0
            ? draft.individualLines
            : [
                {
                  nameOfWork: draft.nameOfWork || '',
                  workVolume: draft.workVolume || '',
                  unit: draft.unit || 'Sq.ft',
                  rate: draft.rate || '',
                },
              ];

        const rpcLines: Array<{
          work_type_id: string;
          worker_count: number;
          rate_per_worker_paise: number;
          total_amount_paise: number;
          remarks: string | null;
        }> = [];

        const workNamesSummary: string[] = [];

        const usedTradeIdsInBatch = new Set<string>();
        for (const item of indLines) {
          const volume = parseFloat(item.workVolume || '0') || 0;
          const rate = parseFloat(item.rate || '0') || 0;
          const totalPaise = Math.round(volume * rate * 100);
          if (totalPaise <= 0) {
            throw new Error('Enter a valid work volume and rate for all work items');
          }

          const workName = item.nameOfWork?.trim() || 'General Labour Work';
          workNamesSummary.push(workName);

          let workTypeId = '';
          const { data: existingWt } = await supabase
            .from('work_types')
            .select('id')
            .ilike('name', workName)
            .maybeSingle();

          if (existingWt) {
            workTypeId = existingWt.id;
          } else {
            const { data: newWt, error: wtErr } = await supabase
              .from('work_types')
              .insert({
                company_id: actor.companyId,
                name: workName,
                sort_order: 100,
              })
              .select('id')
              .single();
            if (wtErr) throw new Error(wtErr.message);
            workTypeId = newWt.id;
          }

          const uniqueTradeId = await resolveUniqueTradeId(
            actor.companyId,
            draft.projectId,
            draft.subcontractorId,
            draft.date,
            workTypeId,
            undefined,
            usedTradeIdsInBatch,
          );
          usedTradeIdsInBatch.add(uniqueTradeId);

          const lineNote = draft.remarks?.trim() || null;

          rpcLines.push({
            work_type_id: uniqueTradeId,
            worker_count: 1,
            rate_per_worker_paise: totalPaise,
            total_amount_paise: totalPaise,
            remarks: lineNote,
          });
        }

        const overallNote = draft.remarks?.trim() || null;

        // 1. Create Subcontractor Labour Batch via RPC
        const { data: batchId, error: batchErr } = await supabase.rpc(
          'create_subcontractor_labour_batch',
          {
            p_project_id: draft.projectId,
            p_subcontractor_id: draft.subcontractorId,
            p_worked_on: draft.date,
            p_lines: rpcLines,
            p_remarks: overallNote || undefined,
          },
        );

        if (batchErr) throw new Error(batchErr.message);

        // 2. If money was paid to sub contractor on the same entry
        const paidPaise = draft.paidAmountRupees.trim() ? toPaise(draft.paidAmountRupees) : 0;
        if (paidPaise > 0) {
          const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
          await supabase.from('subcontractor_payments').insert({
            company_id: actor.companyId,
            subcontractor_id: draft.subcontractorId,
            project_id: draft.projectId,
            paid_on: draft.date,
            amount_paise: paidPaise,
            reference_no: batchId,
            mode: modeVal,
            remarks: draft.remarks?.trim() || null,
            approval_status: 'approved',
          });
        }

        return batchId;
      }

      // Resolve any custom work names to valid work_type_id
      for (const l of draft.lines) {
        if ((l.is_custom || l.work_type_id === '__other__') && l.custom_work_name?.trim()) {
          const customName = l.custom_work_name.trim();
          const { data: existingWt } = await supabase
            .from('work_types')
            .select('id')
            .ilike('name', customName)
            .limit(1)
            .maybeSingle();

          if (existingWt) {
            l.work_type_id = existingWt.id;
          } else {
            const { data: newWt, error: wtErr } = await supabase
              .from('work_types')
              .insert({
                company_id: actor.companyId,
                name: customName,
                sort_order: 100,
              })
              .select('id')
              .single();
            if (wtErr) throw new Error(wtErr.message);
            l.work_type_id = newWt.id;
          }
        }
      }

      // Standard / Weekly Credit with Direct Amount or Lines
      let validLines = draft.lines.filter(
        (l) => l.work_type_id && (Number(l.worker_count) > 0 || parseFloat(l.daily_rate || '0') > 0),
      );

      // If weekly credit entered with direct credit amount
      if (validLines.length === 0 && draft.creditAmountRupees && parseFloat(draft.creditAmountRupees) > 0) {
        const creditPaise = toPaise(draft.creditAmountRupees);
        const { data: defaultWt } = await supabase
          .from('work_types')
          .select('id')
          .eq('is_active', true)
          .order('sort_order')
          .limit(1)
          .single();

        if (defaultWt) {
          validLines = [
            {
              work_type_id: defaultWt.id,
              worker_count: '1',
              daily_rate: draft.creditAmountRupees,
              totalAmountPaise: creditPaise,
              remarks: draft.remarks.trim(),
            },
          ];
        }
      }

      if (validLines.length === 0) {
        throw new Error('Please add at least one work name with weekly rate or enter credit amount');
      }

      // Check unique trade per visit rule
      const tradesSet = new Set<string>();
      for (const l of validLines) {
        if (tradesSet.has(l.work_type_id)) {
          throw new Error('Each work trade can only be added once per visit. Please combine worker counts for the same trade.');
        }
        tradesSet.add(l.work_type_id);
      }

      const usedTradeIdsInBatch = new Set<string>();
      const rpcLines: Array<{
        work_type_id: string;
        worker_count: number;
        rate_per_worker_paise: number | null;
        total_amount_paise: number | null;
        remarks: string | null;
      }> = [];

      for (const l of validLines) {
        const count = Number(l.worker_count);
        const ratePaise = l.daily_rate.trim() ? toPaise(l.daily_rate) : null;
        const totalPaise = ratePaise !== null ? lineAmount(count, ratePaise) : null;

        const uniqueTradeId = await resolveUniqueTradeId(
          actor.companyId,
          draft.projectId,
          draft.subcontractorId,
          draft.date,
          l.work_type_id,
          undefined,
          usedTradeIdsInBatch,
        );
        usedTradeIdsInBatch.add(uniqueTradeId);

        rpcLines.push({
          work_type_id: uniqueTradeId,
          worker_count: count,
          rate_per_worker_paise: ratePaise,
          total_amount_paise: totalPaise,
          remarks: l.remarks.trim() || null,
        });
      }

      // 1. Create Subcontractor Labour Batch via RPC
      const { data: batchId, error: batchErr } = await supabase.rpc(
        'create_subcontractor_labour_batch',
        {
          p_project_id: draft.projectId,
          p_subcontractor_id: draft.subcontractorId,
          p_worked_on: draft.date,
          p_lines: rpcLines,
          p_remarks: draft.remarks.trim() || undefined,
        },
      );

      if (batchErr) throw new Error(batchErr.message);

      // 2. If money was paid to sub contractor, record in subcontractor_payments
      const paidPaise = draft.paidAmountRupees.trim() ? toPaise(draft.paidAmountRupees) : 0;
      if (paidPaise > 0) {
        const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
        await supabase.from('subcontractor_payments').insert({
          company_id: actor.companyId,
          subcontractor_id: draft.subcontractorId,
          project_id: draft.projectId,
          paid_on: draft.date,
          amount_paise: paidPaise,
          reference_no: batchId,
          mode: modeVal,
          remarks: draft.remarks.trim() || null,
          approval_status: 'approved',
        });
      }

      return batchId;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['subcontractor-transactions'] }),
        qc.invalidateQueries({ queryKey: ['subcontractor-balance-summary'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['subcontractors'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}

export function useUpdateSubcontractorLabour() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: SubcontractorLabourDraft }) => {
      if (!actor) throw new Error('Not signed in');
      if (!draft.projectId) throw new Error('Choose a project');
      if (!draft.subcontractorId) throw new Error('Choose a sub-contractor');
      if (!draft.date) throw new Error('Select a date');

      // Standalone Debit update
      if (draft.txType === 'debit') {
        const amtPaise = toPaise(draft.paidAmountRupees);
        if (amtPaise <= 0) throw new Error('Enter a valid payment amount greater than zero');
        const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
        const { error } = await supabase
          .from('subcontractor_payments')
          .update({
            project_id: draft.projectId,
            subcontractor_id: draft.subcontractorId,
            paid_on: draft.date,
            amount_paise: amtPaise,
            mode: modeVal,
            remarks: draft.remarks.trim() || null,
          })
          .eq('id', id);

        if (error) throw new Error(error.message);
        return id;
      }

      // Individual Piece-rate Update
      if (draft.entryMode === 'individual') {
        const indLines =
          draft.individualLines && draft.individualLines.length > 0
            ? draft.individualLines
            : [
                {
                  nameOfWork: draft.nameOfWork || '',
                  workVolume: draft.workVolume || '',
                  unit: draft.unit || 'Sq.ft',
                  rate: draft.rate || '',
                },
              ];

        const insertLines: Array<{
          company_id: string;
          project_id: string;
          subcontractor_id: string;
          batch_id: string;
          work_type_id: string;
          worker_count: number;
          worked_on: string;
          rate_per_worker_paise: number;
          total_amount_paise: number;
          remarks?: string | null;
        }> = [];

        const workNamesSummary: string[] = [];

        const usedTradeIdsInBatch = new Set<string>();
        for (const item of indLines) {
          const volume = parseFloat(item.workVolume || '0') || 0;
          const rate = parseFloat(item.rate || '0') || 0;
          const totalPaise = Math.round(volume * rate * 100);
          if (totalPaise <= 0) {
            throw new Error('Enter a valid work volume and rate for all work items');
          }

          const workName = item.nameOfWork?.trim() || 'General Labour Work';
          workNamesSummary.push(workName);

          let workTypeId = '';
          const { data: existingWt } = await supabase
            .from('work_types')
            .select('id')
            .ilike('name', workName)
            .maybeSingle();

          if (existingWt) {
            workTypeId = existingWt.id;
          } else {
            const { data: newWt, error: wtErr } = await supabase
              .from('work_types')
              .insert({
                company_id: actor.companyId,
                name: workName,
                sort_order: 100,
              })
              .select('id')
              .single();
            if (wtErr) throw new Error(wtErr.message);
            workTypeId = newWt.id;
          }

          const uniqueTradeId = await resolveUniqueTradeId(
            actor.companyId,
            draft.projectId,
            draft.subcontractorId,
            draft.date,
            workTypeId,
            id,
            usedTradeIdsInBatch,
          );
          usedTradeIdsInBatch.add(uniqueTradeId);

          const unitStr = item.unit || 'Sq.ft';
          const lineNote = draft.remarks?.trim() || null;

          insertLines.push({
            company_id: actor.companyId,
            project_id: draft.projectId,
            subcontractor_id: draft.subcontractorId,
            batch_id: id,
            work_type_id: uniqueTradeId,
            worker_count: 1,
            worked_on: draft.date,
            rate_per_worker_paise: totalPaise,
            total_amount_paise: totalPaise,
            remarks: lineNote || undefined,
          });
        }

        const overallNote = draft.remarks?.trim() || null;

        // 1. Update batch
        const { error: batchErr } = await supabase
          .from('subcontractor_labour_batches')
          .update({
            project_id: draft.projectId,
            subcontractor_id: draft.subcontractorId,
            worked_on: draft.date,
            remarks: overallNote,
          })
          .eq('id', id);

        if (batchErr) throw new Error(batchErr.message);

        // 2. Remove old lines and insert updated lines
        await supabase.from('subcontractor_labour').delete().eq('batch_id', id);

        const { error: linesErr } = await supabase.from('subcontractor_labour').insert(insertLines);
        if (linesErr) throw new Error(linesErr.message);

        // 3. Update or insert payment if any
        const paidPaise = draft.paidAmountRupees.trim() ? toPaise(draft.paidAmountRupees) : 0;
        if (paidPaise > 0) {
          const { data: refPay } = await supabase
            .from('subcontractor_payments')
            .select('id')
            .eq('reference_no', id)
            .maybeSingle();

          let targetPayId = refPay?.id;
          if (!targetPayId) {
            const { data: datePay } = await supabase
              .from('subcontractor_payments')
              .select('id')
              .eq('project_id', draft.projectId)
              .eq('subcontractor_id', draft.subcontractorId)
              .eq('paid_on', draft.date)
              .is('reference_no', null)
              .maybeSingle();
            targetPayId = datePay?.id;
          }

          const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
          if (targetPayId) {
            await supabase
              .from('subcontractor_payments')
              .update({
                amount_paise: paidPaise,
                reference_no: id,
                mode: modeVal,
                remarks: draft.remarks.trim() || null,
              })
              .eq('id', targetPayId);
          } else {
            await supabase.from('subcontractor_payments').insert({
              company_id: actor.companyId,
              subcontractor_id: draft.subcontractorId,
              project_id: draft.projectId,
              paid_on: draft.date,
              amount_paise: paidPaise,
              reference_no: id,
              mode: modeVal,
              remarks: draft.remarks.trim() || null,
              approval_status: 'approved',
            });
          }
        }

        return id;
      }

      // Resolve any custom work names to valid work_type_id
      for (const l of draft.lines) {
        if ((l.is_custom || l.work_type_id === '__other__') && l.custom_work_name?.trim()) {
          const customName = l.custom_work_name.trim();
          const { data: existingWt } = await supabase
            .from('work_types')
            .select('id')
            .ilike('name', customName)
            .limit(1)
            .maybeSingle();

          if (existingWt) {
            l.work_type_id = existingWt.id;
          } else {
            const { data: newWt, error: wtErr } = await supabase
              .from('work_types')
              .insert({
                company_id: actor.companyId,
                name: customName,
                sort_order: 100,
              })
              .select('id')
              .single();
            if (wtErr) throw new Error(wtErr.message);
            l.work_type_id = newWt.id;
          }
        }
      }

      let validLines = draft.lines.filter(
        (l) => l.work_type_id && (Number(l.worker_count) > 0 || parseFloat(l.daily_rate || '0') > 0),
      );

      if (validLines.length === 0 && draft.creditAmountRupees && parseFloat(draft.creditAmountRupees) > 0) {
        const creditPaise = toPaise(draft.creditAmountRupees);
        const { data: defaultWt } = await supabase
          .from('work_types')
          .select('id')
          .eq('is_active', true)
          .order('sort_order')
          .limit(1)
          .single();

        if (defaultWt) {
          validLines = [
            {
              work_type_id: defaultWt.id,
              worker_count: '1',
              daily_rate: draft.creditAmountRupees,
              totalAmountPaise: creditPaise,
              remarks: draft.remarks.trim(),
            },
          ];
        }
      }

      if (validLines.length === 0) {
        throw new Error('Please add at least one work name with weekly rate');
      }

      // 1. Update subcontractor_labour_batches
      const { error: batchErr } = await supabase
        .from('subcontractor_labour_batches')
        .update({
          project_id: draft.projectId,
          subcontractor_id: draft.subcontractorId,
          worked_on: draft.date,
          remarks: draft.remarks.trim() || null,
        })
        .eq('id', id);

      if (batchErr) throw new Error(batchErr.message);

      // 2. Remove old lines for this batch
      await supabase.from('subcontractor_labour').delete().eq('batch_id', id);

      // 3. Insert new lines
      const usedTradeIdsInBatch = new Set<string>();
      const newLines: Array<{
        company_id: string;
        project_id: string;
        subcontractor_id: string;
        batch_id: string;
        work_type_id: string;
        worker_count: number;
        worked_on: string;
        rate_per_worker_paise?: number;
        total_amount_paise?: number;
        remarks?: string;
      }> = [];

      for (const l of validLines) {
        const ratePaise = l.daily_rate.trim() ? toPaise(l.daily_rate) : undefined;
        const count = Number(l.worker_count);
        const totalPaise = ratePaise !== undefined ? lineAmount(count, ratePaise) : undefined;

        const uniqueTradeId = await resolveUniqueTradeId(
          actor.companyId,
          draft.projectId,
          draft.subcontractorId,
          draft.date,
          l.work_type_id,
          id,
          usedTradeIdsInBatch,
        );
        usedTradeIdsInBatch.add(uniqueTradeId);

        newLines.push({
          company_id: actor.companyId,
          project_id: draft.projectId,
          subcontractor_id: draft.subcontractorId,
          batch_id: id,
          work_type_id: uniqueTradeId,
          worker_count: count,
          worked_on: draft.date,
          rate_per_worker_paise: ratePaise,
          total_amount_paise: totalPaise,
          remarks: l.remarks.trim() || undefined,
        });
      }

      const { error: linesErr } = await supabase.from('subcontractor_labour').insert(newLines);
      if (linesErr) throw new Error(linesErr.message);

      // 4. Update payments if any
      const paidPaise = draft.paidAmountRupees.trim() ? toPaise(draft.paidAmountRupees) : 0;
      if (paidPaise > 0) {
        const { data: refPay } = await supabase
          .from('subcontractor_payments')
          .select('id')
          .eq('reference_no', id)
          .maybeSingle();

        let targetPayId = refPay?.id;
        if (!targetPayId) {
          const { data: datePay } = await supabase
            .from('subcontractor_payments')
            .select('id')
            .eq('project_id', draft.projectId)
            .eq('subcontractor_id', draft.subcontractorId)
            .eq('paid_on', draft.date)
            .is('reference_no', null)
            .maybeSingle();
          targetPayId = datePay?.id;
        }

        const modeVal = (draft.mode.toLowerCase() || 'cash') as never;
        if (targetPayId) {
          await supabase
            .from('subcontractor_payments')
            .update({
              amount_paise: paidPaise,
              reference_no: id,
              mode: modeVal,
              remarks: draft.remarks.trim() || null,
            })
            .eq('id', targetPayId);
        } else {
          await supabase.from('subcontractor_payments').insert({
            company_id: actor.companyId,
            subcontractor_id: draft.subcontractorId,
            project_id: draft.projectId,
            paid_on: draft.date,
            amount_paise: paidPaise,
            reference_no: id,
            mode: modeVal,
            remarks: draft.remarks.trim() || null,
            approval_status: 'approved',
          });
        }
      }

      return id;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['subcontractor-transactions'] }),
        qc.invalidateQueries({ queryKey: ['subcontractor-balance-summary'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['subcontractors'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}

export function useDeleteSubcontractorTransaction() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (row: SubcontractorTxRow) => {
      await tryRemoveStorageFile((row as unknown as { fileUrl?: string })?.fileUrl);

      const isStandalone = row.lines.length === 0 && row.overallTotalPaise === 0;

      if (isStandalone) {
        const { error: delErr } = await supabase
          .from('subcontractor_payments')
          .delete()
          .eq('id', row.id);
        if (delErr) {
          await supabase
            .from('subcontractor_payments')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', row.id);
        }
      } else {
        await supabase
          .from('subcontractor_payments')
          .delete()
          .eq('reference_no', row.id);

        await supabase
          .from('subcontractor_labour')
          .delete()
          .eq('batch_id', row.id);

        const { error: batchErr } = await supabase
          .from('subcontractor_labour_batches')
          .delete()
          .eq('id', row.id);

        if (batchErr) {
          await supabase
            .from('subcontractor_labour_batches')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', row.id);
          await supabase
            .from('subcontractor_labour')
            .update({ deleted_at: new Date().toISOString() })
            .eq('batch_id', row.id);
        }
      }

      return row.id;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['subcontractor-transactions'] }),
        qc.invalidateQueries({ queryKey: ['subcontractor-balance-summary'] }),
        qc.invalidateQueries({ queryKey: ['subcontractors'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}

/* ============================================================================
 * 4. OTHER EXPENSES (Add Expense)
 * ============================================================================ */

export interface ExpenseTxRow {
  id: string;
  date: string;
  projectId: string;
  projectName: string;
  categoryId: string | null;
  categoryName: string;
  title: string;
  amountPaise: number;
  paidTo: string | null;
  billNo: string | null;
  remarks: string | null;
  status: 'pending' | 'approved' | 'rejected';
}

export interface ExpenseDraft {
  date: string;
  billDate?: string;
  clientId?: string;
  projectId: string;
  categoryId: string;
  customCategory?: string;
  title?: string;
  amountRupees: string;
  paidTo: string;
  billNo: string;
  mode: string;
  remarks: string;
}

export function useExpenseTransactions(search = '', projectId = '') {
  return useQuery({
    queryKey: ['expense-transactions', search, projectId],
    refetchInterval: 4000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ExpenseTxRow[]> => {
      let q = supabase
        .from('expenses')
        .select(`
          id, spent_on, title, amount_paise, paid_to, bill_no, remarks, approval_status, project_id, category_id,
          projects(name), expense_categories(name)
        `)
        .is('deleted_at', null)
        .order('spent_on', { ascending: false });

      if (projectId) q = q.eq('project_id', projectId);

      const rows = unwrap(await q);

      return rows.map((r) => {
        const proj = r.projects as unknown as { name: string } | null;
        const cat = r.expense_categories as unknown as { name: string } | null;

        let customCat = '';
        if (!cat?.name) {
          const match = r.remarks?.match(/(?:\[Category:\s*|Category:\s*)([^\]|\n\r]+)\]?/i);
          if (match && match[1]) {
            customCat = match[1].trim();
          } else if (r.title && r.title !== 'Expense') {
            customCat = r.title;
          }
        }

        const categoryName = cat?.name || customCat || 'General Expense';

        return {
          id: r.id,
          date: r.spent_on,
          projectId: r.project_id,
          projectName: proj?.name ?? '—',
          categoryId: r.category_id,
          categoryName,
          title: r.title,
          amountPaise: Number(r.amount_paise),
          paidTo: r.paid_to,
          billNo: r.bill_no,
          remarks: cleanDisplayRemarks(r.remarks),
          status: r.approval_status as ExpenseTxRow['status'],
        };
      }).filter((r) => {
        if (!search.trim()) return true;
        const term = search.toLowerCase();
        return (
          r.projectName.toLowerCase().includes(term) ||
          r.categoryName.toLowerCase().includes(term) ||
          r.title.toLowerCase().includes(term) ||
          (r.paidTo && r.paidTo.toLowerCase().includes(term)) ||
          (r.billNo && r.billNo.toLowerCase().includes(term)) ||
          (r.remarks && r.remarks.toLowerCase().includes(term))
        );
      });
    },
  });
}

export function useRecordExpense() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async (draft: ExpenseDraft) => {
      if (!actor) throw new Error('Not signed in');
      if (!draft.projectId) throw new Error('Choose a project');
      if (!draft.date) throw new Error('Select a date');
      const amt = Number(draft.amountRupees.replace(/[₹,\s]/g, ''));
      if (!amt || amt <= 0) throw new Error('Enter a valid amount');

      const isOther = draft.categoryId === '__other__';
      const categoryId = isOther ? null : (draft.categoryId || null);
      const customCat = isOther && draft.customCategory?.trim() ? draft.customCategory.trim() : '';

      let fullRemarks = draft.remarks.trim();
      if (customCat) {
        fullRemarks = fullRemarks.includes('[Category: ')
          ? fullRemarks.replace(/\[Category: .*?\]/, `[Category: ${customCat}]`)
          : `[Category: ${customCat}] ${fullRemarks}`.trim();
      }

      const finalTitle = draft.title?.trim() || customCat || 'Expense';

      const { data, error } = await supabase
        .from('expenses')
        .insert({
          company_id: actor.companyId,
          project_id: draft.projectId,
          category_id: categoryId,
          spent_on: draft.billDate || draft.date,
          title: finalTitle,
          amount_paise: toPaise(draft.amountRupees),
          paid_to: draft.paidTo.trim() || null,
          bill_no: draft.billNo.trim() || null,
          remarks: fullRemarks || null,
          approval_status: 'approved',
        })
        .select('id')
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['expense-transactions'] }),
        qc.invalidateQueries({ queryKey: ['expenses'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}

export function useUpdateExpense() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: ExpenseDraft }) => {
      if (!draft.projectId) throw new Error('Choose a project');
      if (!draft.date) throw new Error('Select a date');
      const amt = Number(draft.amountRupees.replace(/[₹,\s]/g, ''));
      if (!amt || amt <= 0) throw new Error('Enter a valid amount');

      const isOther = draft.categoryId === '__other__';
      const categoryId = isOther ? null : (draft.categoryId || null);
      const customCat = isOther && draft.customCategory?.trim() ? draft.customCategory.trim() : '';

      let fullRemarks = draft.remarks.trim();
      if (customCat) {
        fullRemarks = fullRemarks.includes('[Category: ')
          ? fullRemarks.replace(/\[Category: .*?\]/, `[Category: ${customCat}]`)
          : `[Category: ${customCat}] ${fullRemarks}`.trim();
      } else if (!isOther && fullRemarks.includes('[Category: ')) {
        fullRemarks = fullRemarks.replace(/\[Category: .*?\]\s*-?\s*/gi, '').trim();
      }

      const finalTitle = draft.title?.trim() || customCat || 'Expense';

      const { data, error } = await supabase
        .from('expenses')
        .update({
          project_id: draft.projectId,
          category_id: categoryId,
          spent_on: draft.billDate || draft.date,
          title: finalTitle,
          amount_paise: toPaise(draft.amountRupees),
          paid_to: draft.paidTo.trim() || null,
          bill_no: draft.billNo.trim() || null,
          remarks: fullRemarks || null,
        })
        .eq('id', id)
        .select('id')
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['expense-transactions'] }),
        qc.invalidateQueries({ queryKey: ['expenses'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}

export function useDeleteExpenseTransaction() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (row: ExpenseTxRow) => {
      await tryRemoveStorageFile((row as unknown as { receiptUrl?: string })?.receiptUrl);

      const { error: delErr } = await supabase
        .from('expenses')
        .delete()
        .eq('id', row.id);

      if (delErr) {
        const { error: softErr } = await supabase
          .from('expenses')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', row.id);

        if (softErr) throw new Error(delErr.message || softErr.message);
      }
      return row.id;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['expense-transactions'] }),
        qc.invalidateQueries({ queryKey: ['expenses'] }),
        qc.invalidateQueries({ queryKey: ['project'] }),
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });
}


